import classNames from 'classnames';
import React, { useState, useEffect } from 'react';

import Modal from '~components/ModalDialog/ModalDialog';
import ZipForm from '~components/ZipCodeForm/ZipCodeForm';
import constants from '~globals/constants';
import { fireCloseModal } from '~globals/tracking';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import { toggleZipFormModal } from '~redux/Actions/DisplayActions';
import {
    updateFilter,
    updateZipModalCta,
    updateZipModalHeading,
} from '~redux/Actions/GlobalActions';

const ZipFormModal = () => {
    const isModalOpen = useAppSelector((state) => state.display.zipModalOpen);
    const carrierId = useAppSelector((state) => state.global.filter);
    const { customCta, customDestination, customHeading } = useAppSelector(
        (state) => state.global.zipModalConfig
    );

    const dispatch = useAppDispatch();

    const [heading, setHeading] = useState<string | React.ReactElement | null>();
    const [title, setTitle] = useState(
        'Enter your ZIP Code to see plans.' as string | React.ReactElement
    );
    const [isGeneric, setIsGeneric] = useState(false);
    const [hasCancelButton, setHasCancelButton] = useState(!!customDestination && !customHeading);

    const btnCta = customCta || (!customDestination && 'Save') || undefined;

    const handleClose = () => {
        dispatch(toggleZipFormModal(false));
        dispatch(updateFilter(''));
        dispatch(updateZipModalHeading(''));
        dispatch(updateZipModalCta(''));
        fireCloseModal();
    };

    const waLink: string = 'Click:' + process.env.GATSBY_ANALYTICS_SITE_GROUP + ':ZipModal';

    useEffect(() => {
        let carrierName = null;
        const CARRIER_IDS = constants.CARRIER_ID;
        switch (carrierId) {
            case '6:3':
                carrierName = 'Aetna';
                break;
            case '6:113':
                carrierName = 'Anthem';
                break;
            case '6:124':
                carrierName = 'Wellcare';
                break;
            case '6:108':
                carrierName = 'United Healthcare';
                break;
            case '6:66':
                carrierName = 'Humana';
                break;
            case CARRIER_IDS.cigna:
                carrierName = 'Cigna';
                break;
            case '':
                setIsGeneric(true);
                setHasCancelButton(false);
                setHeading('Compare plans in your area');
                setTitle('Enter your ZIP code to shop for plans near you.');
                return;
        }
        if (carrierName !== null) {
            setTitle(
                <h1
                    id="modalTitle"
                    className={classNames([
                        'font-bold text-center text-black text-xl md:text-2xl',
                        'items-center justify-center',
                        'w-full py-4 pl-4 pr-10 md:px-12 md:shadow max-w-[455px]',
                    ])}
                >
                    {'Enter your ZIP Code to see plans from '}
                    <span className="text-blue">{carrierName}</span>.
                </h1>
            );
        }
    }, [carrierId]);

    return (
        <Modal
            isOpen={isModalOpen}
            setIsOpen={handleClose}
            heading={heading}
            title={customHeading || title}
            isGeneric={isGeneric}
        >
            <div className={classNames('flex relative', isGeneric ? 'flex-col py-3' : 'flex-row')}>
                {!isGeneric && (
                    <div className="border-green-divider w-full mb-4 border-t md:border-t-0 md:my-0 print:hidden" />
                )}
                <div
                    className={classNames(
                        'mx-auto',
                        isGeneric ? 'max-w-96 w-full' : 'mb-8 md:mb-0 max-w-72'
                    )}
                >
                    <ZipForm
                        buttonWaLink={waLink}
                        buttonTitle={isGeneric ? 'Compare plans' : btnCta}
                        qaValue="ZipModal"
                        redirect={{ toUrl: customDestination }}
                        opts={{ forceVerticalLayout: true, btnStyle: 'orange' }}
                        isZipLocked={true}
                        isGeneric={isGeneric}
                    />
                </div>
                {hasCancelButton && (
                    <button
                        data-qa="ModalCloseButton"
                        onClick={handleClose}
                        className="w-full h-6 md:mt-4 mb-6 md:mb-2 text-blue font-semibold"
                    >
                        Cancel
                    </button>
                )}
            </div>
        </Modal>
    );
};

export default ZipFormModal;
